// UI strings of "Meidän nimet" (2026-10-07; names/, engine/src/our-names.ts): your names in High
// Valyrian, taught and voted on. Finnish (default) and English side by side, the same shape. The
// people's names come from the server (names/people.ts) as arguments, already inflected where a
// Finnish case is needed (gen = "Axelin", par = "Axelia").
import { defineArea } from '../area';

type OptionId = 'aksel' | 'akseli' | 'rosa-1' | 'rosa-borrow' | 'lyksion' | 'aeksar' | 'rekomisaros' | 'rekazmia' | 'rhaesa' | 'vaenyra';

export const namesFi = {
  title: 'Meidän nimet',
  /** A question's title, with the owner's name in the genitive. */
  question: {
    real: (gen: string) => `${gen} nimi`,
    cosplay: (gen: string) => `${gen} Targaryen-nimi`,
  },
  /** The vote page's question. */
  ask: {
    'axel-real': 'Aksel vai Akseli?',
    'roosa-real': 'Taipuuko Rōsa kuin Rhaenyra vai kuin lainasana?',
    cosplay: (gen: string) => `Mikä on ${gen} nimi Targaryenina?`,
  },

  // ---- decision ----
  decidedAgree: 'Päätetty: äänestitte samoin.',
  decidedOwner: (ownerGen: string) => `Päätetty: äänet erosivat, joten ${ownerGen} oma ääni ratkaisi.`,
  decidedOwnerMe: 'Päätetty: äänet erosivat, joten oma äänesi ratkaisi.',
  waitingYou: 'Odottaa sinun ääntäsi.',
  waitingFor: (gen: string) => `Odottaa ${gen} ääntä.`,
  waitingBoth: 'Odottaa teidän molempien ääntä.',
  suggestion: 'Ehdotus, ei vielä päätetty',
  rule: 'Kumpikin äänestää yhtä vaihtoehtoa. Jos äänet eroavat, nimen omistaja päättää. Ääntä voi vaihtaa myöhemmin.',
  vote: 'Äänestä',
  voted: 'Äänestit tätä',
  yourVote: 'sinun äänesi',
  theirVote: (gen: string) => `${gen} ääni`,
  others: 'Muut vaihtoehdot',
  chosen: 'Valittu',
  openVote: 'Äänestä nimistä',
  voteLeft: (n: number) => (n === 0 ? 'Kaikki nimet on päätetty' : n === 1 ? '1 nimi odottaa päätöstä' : `${n} nimeä odottaa päätöstä`),
  readOnly: (a: string, b: string) => `Äänestää voivat vain ${a} ja ${b}.`,

  // ---- teaching ----
  spelling: 'Kirjoitus',
  declension: 'Taivutus',
  voices: 'Ääntäminen',
  inflects: (decl: string, gender: string, model: string) => `${decl}. deklinaatio, ${gender}, taipuu kuten ${model}`,
  vocative: 'Vokatiivi',
  vocativeNote: 'Kun puhuttelet toista, nimi on vokatiivissa.',
  tableHeading: 'Taivutus yksikössä',
  /** The vote page: the options of a given name side by side. */
  compare: {
    heading: 'Vaihtoehdot rinnakkain',
    legend: 'Korostetuissa sijoissa vaihtoehdot eroavat.',
    caseCol: 'Sija',
    like: (model: string) => `kuten ${model}`,
  },
  why: {
    x: 'Valyriassa ei ole x-kirjainta: äänne [ks] kirjoitetaan k + s. Petersonin sanakirjassa George R. R. Martinin nimien x on aina ks:',
    l: 'Valyrian substantiivit eivät pääty l:ään, joten Aksel on lainanimi. Peterson taivuttaa tällaisen nimen kuudennessa deklinaatiossa kuten Rhaegalin, ja se on aina kuusukuinen.',
    i: 'Peterson sallii lainasanan nominatiiviin i:n (buzdar tai buzdari). Silloin nominatiivi on Akseli, muut sijat ovat samat.',
    oo: 'Pitkä vokaali merkitään viivalla, ei kahdella kirjaimella: oo → ō.',
    a: 'Rōsa päättyy a:han kuten Rhaenyra, joten se taipuu ensimmäisessä deklinaatiossa kuusukuisena. Vokatiivi on Rōsus, kuten Rhaenyrus.',
    borrow:
      'Petersonin mukaan vieras a-loppuinen sana (mhysa) voi taipua myös lainasanojen tapaan: a jää pois muista sijoista kuin nominatiivista. Silloin akkusatiivi on Rōsi ja vokatiivi Rōsis.',
  },
  source: (date: string) => `Peterson, ${date}`,
  sourceLabel: 'Lähde',

  // ---- the forms you use ----
  used: 'Näin käytätte nimiä',
  model: 'Mallilause',
  sentence: {
    call: (name: string) => `${name}! (puhutellessa)`,
    /** who loves whom: the lover in the nominative, the loved one in the partitive. */
    loves: (lover: string, lovedPar: string) => `${lover} rakastaa ${lovedPar}.`,
    intro: (hvName: string) => `Minä olen ${hvName} Targaryenin suvusta.`,
  },
  sentenceGroup: { call: 'Puhuttelu', loves: 'Rakkaudesta', intro: 'Hahmossa' },
  record: 'Äänitä',
  closeRecord: 'Sulje äänitys',

  // ---- the Targaryen names ----
  cosplay: 'Targaryen-nimet',
  built: 'Rakennettu',
  meaning: 'Merkitys',
  noMeaning: 'ei merkitystä, pelkkä nimi',
  option: {
    aksel: { built: 'Nominatiivi ilman päätettä, kuten Rhaegal.', meaning: '' },
    akseli: { built: 'Nominatiivissa lainasanan i-pääte, kuten Dothraki.', meaning: '' },
    'rosa-1': { built: 'Taipuu kuten Rhaenyra.', meaning: '' },
    'rosa-borrow': { built: 'Taipuu lainasanojen tapaan, kuten Petersonin mukaan mhysa voi taipua.', meaning: '' },
    lyksion: { built: 'lyks ’rauha’ + -ion, Targaryen- ja lohikäärmenimien pääte (Viserion, Tyrion, Velarion)', meaning: '’rauhan’ (väljästi)' },
    aeksar: { built: 'Rhaegarin muoto, Aegonin ja Aemonin Ae- sekä Aksel-nimen k ja s', meaning: '' },
    rekomisaros: {
      built: 'rēko ’ruusu’ + mīsaros ’suojelija’ (verbistä mīsagon), kuten Petersonin Valemīsaros ’ihmisten suojelija’',
      meaning: '’ruusun suojelija’',
    },
    rekazmia: { built: 'rēko ’ruusu’ + -āzma ’suuri’ + -ia, kuten Petersonin Hūrāzmia (hūra ’kuu’)', meaning: 'suunnilleen ’suuri ruusu’' },
    rhaesa: { built: 'Rhaenyran ja Rhaenysin Rhae- sekä Rōsa-nimen -sa', meaning: '' },
    vaenyra: { built: 'Vaellan ja Vaegonin Vae- sekä Rhaenyran -nyra', meaning: '' },
  } as Record<OptionId, { built: string; meaning: string }>,

  // ---- own proposals ----
  own: {
    open: 'Ehdota omaa nimeä',
    field: 'Nimi valyriaksi',
    note: 'Mitä se tarkoittaa tai mistä se on rakennettu',
    save: 'Lisää vaihtoehdoksi',
    cancel: 'Peru',
    ok: (decl: string, gender: string, stress: string) => `Kelpaa: ${decl}. deklinaatio, ${gender}, paino ${stress}.`,
    low: 'Taivutusluokka on arvaus: nimen loppu ei sovi varmasti mihinkään luokkaan.',
    warning: 'Huomaa',
    rejected: 'Ei kelpaa',
    problem: {
      'too-long': 'Nimi on liian pitkä (enintään 24 kirjainta).',
      'no-class': 'Nimelle ei löydy taivutusluokkaa.',
      duplicate: 'Tämä nimi on jo vaihtoehtona.',
      'no-stress': 'Nimen painoa ei voi määrittää.',
      shape: 'Kirjoita nimi yhtenä sanana pelkin kirjaimin.',
    },
    byYou: 'Sinun ehdotuksesi',
    by: (name: string) => `${name} ehdotti`,
    remove: 'Poista ehdotus',
  },
  lessonLink: 'Omat nimenne valyriaksi',
};

export const namesEn: typeof namesFi = {
  title: 'Our names',
  question: {
    real: (gen) => `${gen} name`,
    cosplay: (gen) => `${gen} Targaryen name`,
  },
  ask: {
    'axel-real': 'Aksel or Akseli?',
    'roosa-real': 'Does Rōsa inflect like Rhaenyra or like a borrowing?',
    cosplay: (gen) => `What is ${gen} name as a Targaryen?`,
  },
  decidedAgree: 'Decided: you both voted the same.',
  decidedOwner: (ownerGen) => `Decided: your votes differed, so ${ownerGen} own vote decided.`,
  decidedOwnerMe: 'Decided: your votes differed, so your own vote decided.',
  waitingYou: 'Waiting for your vote.',
  waitingFor: (gen) => `Waiting for ${gen} vote.`,
  waitingBoth: 'Waiting for both your votes.',
  suggestion: 'Suggestion, not decided yet',
  rule: 'Each of you votes for one option. If your votes differ, the name’s owner decides. A vote can be changed later.',
  vote: 'Vote',
  voted: 'You voted for this',
  yourVote: 'your vote',
  theirVote: (gen) => `${gen} vote`,
  others: 'Other options',
  chosen: 'Chosen',
  openVote: 'Vote on the names',
  voteLeft: (n) => (n === 0 ? 'Every name is decided' : n === 1 ? '1 name waits for a decision' : `${n} names wait for a decision`),
  readOnly: (a, b) => `Only ${a} and ${b} vote.`,
  spelling: 'Spelling',
  declension: 'Declension',
  voices: 'Pronunciation',
  inflects: (decl, gender, model) => `declension ${decl}, ${gender}, inflects like ${model}`,
  vocative: 'Vocative',
  vocativeNote: 'When you address each other, the name is in the vocative.',
  tableHeading: 'Singular',
  compare: {
    heading: 'The options side by side',
    legend: 'The options differ in the highlighted cases.',
    caseCol: 'Case',
    like: (model) => `like ${model}`,
  },
  why: {
    x: 'High Valyrian has no x: the sound [ks] is written k + s. In Peterson’s dictionary the x of George R. R. Martin’s names is always ks:',
    l: 'High Valyrian nouns do not end in l, so Aksel is a borrowed name. Peterson inflects such a name in the sixth declension like Rhaegal, and it is always lunar.',
    i: 'Peterson allows a borrowing an -i in the nominative (buzdar or buzdari). Then the nominative is Akseli; the other cases are the same.',
    oo: 'A long vowel is written with a macron, not with two letters: oo → ō.',
    a: 'Rōsa ends in a like Rhaenyra, so it is first declension, lunar. The vocative is Rōsus, like Rhaenyrus.',
    borrow:
      'Peterson says a foreign word in -a (mhysa) may also inflect as a borrowing, dropping the a outside the nominative. Then the accusative is Rōsi and the vocative Rōsis.',
  },
  source: (date) => `Peterson, ${date}`,
  sourceLabel: 'Source',
  used: 'Using your names',
  model: 'Model sentence',
  sentence: {
    call: (name) => `${name}! (addressing)`,
    loves: (lover, loved) => `${lover} loves ${loved}.`,
    intro: (hvName) => `I am ${hvName} of House Targaryen.`,
  },
  sentenceGroup: { call: 'Addressing each other', loves: 'Love', intro: 'In character' },
  record: 'Record',
  closeRecord: 'Close recording',
  cosplay: 'Targaryen names',
  built: 'Built from',
  meaning: 'Meaning',
  noMeaning: 'no meaning, just a name',
  option: {
    aksel: { built: 'The nominative without an ending, like Rhaegal.', meaning: '' },
    akseli: { built: 'The nominative with the -i of borrowings, like Dothraki.', meaning: '' },
    'rosa-1': { built: 'Inflects like Rhaenyra.', meaning: '' },
    'rosa-borrow': { built: 'Inflects as a borrowing, as Peterson says mhysa may.', meaning: '' },
    lyksion: { built: 'lyks ‘peace’ + -ion, the ending of Targaryen and dragon names (Viserion, Tyrion, Velarion)', meaning: '‘of peace’ (loosely)' },
    aeksar: { built: 'the shape of Rhaegar, the Ae- of Aegon and Aemon, and the k and s of Aksel', meaning: '' },
    rekomisaros: {
      built: 'rēko ‘rose’ + mīsaros ‘protector’ (from mīsagon), like Peterson’s Valemīsaros ‘protector of men’',
      meaning: '‘guardian of the rose’',
    },
    rekazmia: { built: 'rēko ‘rose’ + -āzma ‘great’ + -ia, like Peterson’s Hūrāzmia (hūra ‘moon’)', meaning: 'roughly ‘great rose’' },
    rhaesa: { built: 'the Rhae- of Rhaenyra and Rhaenys and the -sa of Rōsa', meaning: '' },
    vaenyra: { built: 'the Vae- of Vaella and Vaegon and the -nyra of Rhaenyra', meaning: '' },
  },
  own: {
    open: 'Propose a name of your own',
    field: 'The name in High Valyrian',
    note: 'What it means or what it is built from',
    save: 'Add as an option',
    cancel: 'Cancel',
    ok: (decl, gender, stress) => `Fine: declension ${decl}, ${gender}, stress ${stress}.`,
    low: 'The declension is a guess: the ending fits no class for sure.',
    warning: 'Note',
    rejected: 'Not possible',
    problem: {
      'too-long': 'The name is too long (24 letters at most).',
      'no-class': 'No declension fits the name.',
      duplicate: 'This name is already an option.',
      'no-stress': 'The stress of the name cannot be placed.',
      shape: 'Write the name as one word, letters only.',
    },
    byYou: 'Your proposal',
    by: (name) => `${name} proposed it`,
    remove: 'Withdraw',
  },
  lessonLink: 'Your names in High Valyrian',
};

// Messages.names (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('names', namesFi, namesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof namesFi> {}
}
